import { connect } from 'react-redux';
import {
  ReactFlow,
  Controls,
  Background,
  useNodesState,
  ReactFlowProvider,
  MiniMap,
  useReactFlow,
  useEdgesState
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { withTranslation } from 'react-i18next';
import styles from 'components/knowledgeGraph/graph.scss';

import withEmbedded from 'root/hocs/withEmbedded';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { nodeTypes } from 'components/knowledgeGraph/components/nodes/CustomNode';
import { edgeTypes } from 'components/knowledgeGraph/components/edges/CustomEdges';
import { createNodesFromHierarchy, createEdgesFromHierarchy } from 'components/knowledgeGraph/utils/graphDataUtils';
import { useNodeSubtitleInfo } from 'components/knowledgeGraph/utils/nodeUtils';
import {
  highlightConnections,
  highlightNode,
  highlightEdgeAndNodes,
  removeHighlights,
  highlightChildNode
} from 'components/knowledgeGraph/utils/highlight-utils';
import InfoPanelPopover from 'components/knowledgeGraph/InfoPanelPopover';
import 'components/knowledgeGraph/graph-highlight.scss';
import { computeEdgeStartAndEndPosition } from 'components/knowledgeGraph/utils/edgeUtils';
import { cancelAllEditing } from 'store/modules/nodeEditor';
import { updateNode } from 'store/modules/graph/graph';

// --- Constants ---
const MIN_ZOOM = 0.1;
const ZOOM_THRESHOLD = 0.5;
const TOOLTIP_HIDE_DELAY_MS = 100;
const FOCUS_ANIMATION_DURATION_MS = 400;
const DEFAULT_FOCUS_ZOOM = 1;

// Tooltip positioning offsets
const TOOLTIP_OFFSET_X_FACTOR = 5;
const TOOLTIP_OFFSET_Y = 100;

const KnowledgeGraphFlowInternal = ({
  graph,
  graphSearchResults,
  isEmbedded,
  hideEntitiesSearch,
  hideStudioLink,
  dispatch,
  t,
  nodeUriToFocus,
  onNodeFocused,
  flowNodeStructure,
  nodeEditor,
  style
}) => {
  // --- State ---
  const [tooltipState, setTooltipState] = useState({
    isOpen: false,
    position: { x: 0, y: 0 },
    node: null
  });
  const { subtitleIcon, subtitleText } = useNodeSubtitleInfo(tooltipState.node, t);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges] = useEdgesState([]);
  const { getZoom, setCenter } = useReactFlow();
  const [isSimplifiedView, setIsSimplifiedView] = useState(false);
  const tooltipTimeoutRef = useRef(null);
  const reactFlowInstance = useReactFlow();
  const previousGraphNodes = useRef(graph.nodes);
  const { isSearching, highlighting, query } = graphSearchResults;

  // --- Effects ---
  // Highlight nodes when we search the knowledge graph
  useEffect(() => {
    if (!isSearching && highlighting?.length > 0) {
      const nodesToHighlight = nodes.filter(
        n =>
          highlighting.some(h => h.uri === n.data.node.uri) ||
          n.data.childNodes.some(c => highlighting.some(h => h.uri === c.uri))
      );
      const childNodesToHighlight = nodes
        .flatMap(n => n.data.childNodes)
        .filter(n => highlighting.some(h => h.uri === n.uri));
      removeHighlights();
      nodesToHighlight.forEach(node => highlightNode(node));
      childNodesToHighlight.forEach(node => highlightChildNode(node));
    }
  }, [isSearching, highlighting, nodes]);

  // Remove highlights when the query changes or no results
  useEffect(() => {
    if (!isSearching && (query.length === 0 || !highlighting || highlighting.length === 0)) {
      removeHighlights();
    }
  }, [isSearching, query]);

  // Jump to the "clicked" node e.g. via the KG search dropdown
  useEffect(() => {
    if (!nodeUriToFocus) return;

    // Try finding the node at the top level
    let nodeToFocus = nodes.find(node => node.data.node?.uri === nodeUriToFocus);
    let nodeData = null;

    // If not found, check within child nodes
    if (!nodeToFocus) {
      for (const parentNode of nodes) {
        const childNode = parentNode.data.childNodes?.find(c => c.uri === nodeUriToFocus);
        if (childNode) {
          nodeToFocus = parentNode; // Focus the parent container if a child matches
          nodeData = childNode; // Store the actual child node data for the tooltip
          break;
        }
      }
    } else {
      nodeData = nodeToFocus.data.node; // Store the node data for the tooltip
    }

    if (nodeToFocus) {
      // First center on the node with animation
      setCenter(nodeToFocus.position.x, nodeToFocus.position.y, {
        duration: FOCUS_ANIMATION_DURATION_MS,
        zoom: DEFAULT_FOCUS_ZOOM
      });

      // After animation completes, open the tooltip
      setTimeout(() => {
        // Find the DOM element for this node
        const nodeElement = document.querySelector(`[data-id="${nodeToFocus.id}"]`);
        if (nodeElement) {
          // Get the bounding rect to position tooltip, similar to onChildMouseEnter
          const rect = nodeElement.getBoundingClientRect();

          setTooltipState({
            isOpen: true,
            position: {
              x: rect.right - TOOLTIP_OFFSET_X_FACTOR / getZoom(),
              y: rect.top - TOOLTIP_OFFSET_Y
            },
            node: nodeData || nodeToFocus.data.node
          });
        }

        onNodeFocused();
      }, FOCUS_ANIMATION_DURATION_MS + 50); // Add a small buffer after animation
    }
  }, [nodeUriToFocus, nodes, onNodeFocused, setCenter, getZoom]);

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      clearTimeout(tooltipTimeoutRef.current);
    };
  }, []);

  // --- Callbacks ---

  // Callback: Handle graph movement (zoom/pan) to toggle simplified view
  const onMove = useCallback(() => {
    const currentZoom = getZoom();
    const shouldSimplify = currentZoom < ZOOM_THRESHOLD;
    if (shouldSimplify !== isSimplifiedView) {
      setIsSimplifiedView(shouldSimplify);
    }
  }, [getZoom, isSimplifiedView]);

  // Highlight edges on hover
  const onEdgeMouseEnter = useCallback((event, edge) => {
    highlightEdgeAndNodes(edge, reactFlowInstance);
  }, []);

  // Remove highlights on hover
  const onEdgeMouseLeave = useCallback(() => {
    removeHighlights();
  }, []);

  // Highlight node and it's neighbours on hover
  const onNodeMouseEnter = useCallback((event, node) => {
    highlightConnections(node, reactFlowInstance);
  }, []);

  // Remove highlights & close tooltip when we move away from the node
  const onNodeMouseLeave = useCallback(() => {
    removeHighlights();
    setTooltipState(prevState => ({
      ...prevState,
      isOpen: false
    }));
  }, []);

  // Update "current node" and set tooltip position on hover
  const onChildMouseEnter = useCallback((childNode, event) => {
    // Clear any existing timeout (e.g. when the user moves back from the tooltip to the node we don't want to close the tooltip)
    clearTimeout(tooltipTimeoutRef.current);
    if (event?.currentTarget) {
      const rect = event.currentTarget.getBoundingClientRect();
      setTooltipState(prevState => ({
        ...prevState,
        position: { x: rect.right - TOOLTIP_OFFSET_X_FACTOR / getZoom(), y: rect.top - TOOLTIP_OFFSET_Y }
      }));
    }
    setTooltipState(prevState => ({
      ...prevState,
      node: childNode
    }));
  }, []);

  // Toggle the tooltip on node click
  const onNodeClick = useCallback((childNode, e) => {
    // if we click on a child node, we don't want to also run the click handler of the parent
    e.stopPropagation();
    setTooltipState(prevState => ({
      ...prevState,
      isOpen: !prevState.isOpen,
      node: childNode
    }));
  }, []);

  // Whenever we click on the pane remove highlights & close tooltip
  const onPaneClick = useCallback(() => {
    removeHighlights();
    setTooltipState(prevState => ({
      ...prevState,
      isOpen: false
    }));
  }, []);

  // Hide any open tooltips when drag starts
  const onNodeDragStart = useCallback(() => {
    setTooltipState(prevState => ({
      ...prevState,
      isOpen: false
    }));
  }, []);

  // Jump to position on canvas on minimap click
  const onMiniMapClick = useCallback(
    (event, position) => {
      // Set the center to the clicked position with a smooth animation
      setCenter(position.x, position.y, { duration: FOCUS_ANIMATION_DURATION_MS, zoom: getZoom() });
    },
    [setCenter, getZoom]
  );

  useEffect(() => {
    if (!graph.nodes || !flowNodeStructure) {
      return;
    }

    const newReactFlowNodes = createNodesFromHierarchy(
      flowNodeStructure,
      graph.nodes,
      graph.visualNodeLayoutFlow,
      onChildMouseEnter,
      onNodeClick,
      isSimplifiedView
    );

    // Find out which nodes have changed compared to previous render
    if (previousGraphNodes.current && graph.nodes) {
      const changedNodes = graph.nodes.filter(newNode => {
        // Find corresponding node in previous nodes by URI
        const prevNode = previousGraphNodes.current.find(pNode => pNode.uri === newNode.uri);
        if (!prevNode) {
          // New node that wasn't in previous set
          return true;
        }
        // Check if any relevant properties changed
        return JSON.stringify(prevNode) !== JSON.stringify(newNode);
      });

      if (changedNodes.length > 0 && changedNodes.length < graph.nodes.length / 2) {
        // Small number of nodes changed, use selective updates
        changedNodes.forEach(changedGraphNode => {
          // Find the reactFlowNodes that contain the changed node
          const updatedReactFlowNodes = newReactFlowNodes.filter(
            rfNode =>
              rfNode.data.node?.uri === changedGraphNode.uri ||
              rfNode.data.childNodes.some(c => c.uri === changedGraphNode.uri)
          );

          updatedReactFlowNodes.forEach(n => {
            // Retain the position of the node in case it was moved by the user
            delete n.position;
            // Update the node using the updateNode API
            reactFlowInstance.updateNode(n.id, n);
          });
        });

        // Update reference for next comparison
        previousGraphNodes.current = graph.nodes;
        return; // Skip full update
      }
    }

    // Full update for any of these cases:
    // - Initial render
    // - No previous nodes to compare with
    // - Too many nodes changed (>50%)
    setNodes(newReactFlowNodes);

    // Update reference for next comparison
    previousGraphNodes.current = graph.nodes;
  }, [flowNodeStructure, graph.visualNodeLayoutFlow, isSimplifiedView, graph.nodes]);

  // Construct edges
  const processedEdges = useMemo(() => {
    const getNode = id => nodes.find(node => node.id === id) || null;
    const edges = createEdgesFromHierarchy(graph, isSimplifiedView);
    return computeEdgeStartAndEndPosition(edges, getNode);
  }, [graph, nodes, isSimplifiedView]);

  // Update React Flow edge state
  useEffect(() => {
    setEdges(processedEdges);
  }, [processedEdges]);

  // Callback from when we fetch more information within the tooltip
  const updateNodeFromTooltip = useCallback(
    updatedNode => {
      setTooltipState(prevState => ({
        ...prevState,
        node: updatedNode
      }));
      return updateNode(updatedNode);
    },
    [tooltipState.node]
  );

  return (
    <div className={isEmbedded ? styles.graphEmbedded : styles.graph} style={style}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodeMouseEnter={onNodeMouseEnter}
        onNodeMouseLeave={onNodeMouseLeave}
        onEdgeMouseEnter={onEdgeMouseEnter}
        onEdgeMouseLeave={onEdgeMouseLeave}
        onNodeDragStart={onNodeDragStart}
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
        onMove={onMove}
        proOptions={{ hideAttribution: true }}
        minZoom={MIN_ZOOM}
        fitView
      >
        <Controls showInteractive={false} />
        <Background />
        <MiniMap
          nodeStrokeWidth={3}
          pannable={true}
          zoomable={true}
          className={styles.minimap}
          onClick={onMiniMapClick}
          nodeStrokeColor="var(--primary-color)"
        />
      </ReactFlow>
      <InfoPanelPopover
        PopoverProps={{
          x: tooltipState.position.x,
          y: tooltipState.position.y,
          open: tooltipState.isOpen,
          onMouseEnter: () => {
            // we need to explicitly set the tooltip to open again, as it will get closed due to some previous leave handler
            setTooltipState(prevState => ({
              ...prevState,
              isOpen: true
            }));
          },
          onMouseLeave: () => {
            // Get current editing state
            const isEditing =
              nodeEditor.isEditingTitle || nodeEditor.isEditingDescription || nodeEditor.isEditingSynonyms;

            // Only schedule tooltip closing if not in edit mode
            if (!isEditing) {
              // Hide the tooltip after a delay, because if the user moves directly again on an element of the node we want to keep it open
              tooltipTimeoutRef.current = setTimeout(() => {
                dispatch(cancelAllEditing());
                setTooltipState(prevState => ({
                  ...prevState,
                  isOpen: false
                }));
              }, TOOLTIP_HIDE_DELAY_MS);
            }
          }
        }}
        ContentProps={{
          node: tooltipState.node,
          subtitleIcon: subtitleIcon,
          nodeType: tooltipState.node?.type,
          expandedByDefault: true,
          subtitleText: subtitleText,
          updateNode: updateNodeFromTooltip,
          hideStudioLink: hideStudioLink,
          hideEntitiesSearch: hideEntitiesSearch
        }}
      />
    </div>
  );
};

const KnowledgeGraphFlow = props => (
  <ReactFlowProvider>
    <KnowledgeGraphFlowInternal {...props} />
  </ReactFlowProvider>
);

const mapStateToProps = state => ({
  flowNodeStructure: state.graph.flowNodeStructure,
  graphSearchResults: state.graphSearchResults,
  nodeEditor: state.nodeEditor
});

export default withTranslation('veezoo')(connect(mapStateToProps)(withEmbedded(KnowledgeGraphFlow)));
